<template>
  <div class="anchor-management">
    <el-page-header content="主播信息管理" />

    <!-- 搜索和新增按钮区域 -->
    <div class="search-bar">
      <el-input
        v-model="searchKeyword"
        placeholder="请输入主播账号/主播码搜索"
        style="width: 300px; margin-right: 10px"
        clearable
        @input="handleSearch"
      />
      <el-button type="primary" @click="openAddDialog">新增主播</el-button>
    </div>

    <!-- 主播列表表格 -->
    <el-table
      :data="anchorList"
      border
      stripe
      style="width: 100%; margin-top: 20px"
      @selection-change="handleSelectionChange"
    >
      <el-table-column type="selection" width="55" />
      <el-table-column prop="id" label="ID" width="80" />
      <el-table-column prop="account" label="主播账号" min-width="120" />
      <el-table-column prop="password" label="主播密码" min-width="120" />
      <el-table-column prop="anchorCode" label="主播码" min-width="120" />
      <el-table-column prop="parentAnchorName" label="所属上级主播" min-width="150" />
      <el-table-column label="操作" min-width="200">
        <template #default="scope">
          <el-button size="small" type="primary" @click="openEditDialog(scope.row)">编辑</el-button>
          <el-button size="small" type="danger" @click="handleDelete(scope.row.id)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页控件 -->
    <el-pagination
      v-model:current-page="currentPage"
      v-model:page-size="pageSize"
      :page-sizes="[5, 10, 20]"
      :total="total"
      layout="total, sizes, prev, pager, next, jumper"
      style="margin-top: 20px; text-align: right"
      @size-change="handleSizeChange"
      @current-change="handleCurrentChange"
    />

    <!-- 新增/编辑弹窗 -->
    <el-dialog
      v-model="dialogVisible"
      :title="isEdit ? '编辑主播' : '新增主播'"
      width="500px"
      @close="resetForm"
    >
      <el-form
        ref="anchorFormRef"
        :model="anchorForm"
        :rules="anchorRules"
        label-width="100px"
      >
        <el-form-item label="主播账号" prop="account">
          <el-input v-model="anchorForm.account" placeholder="请输入主播账号" />
        </el-form-item>
        <el-form-item label="主播密码" prop="password">
          <el-input v-model="anchorForm.password" type="password" placeholder="请输入主播密码" />
        </el-form-item>
        <el-form-item label="主播码" prop="anchorCode">
          <el-input v-model="anchorForm.anchorCode" placeholder="请输入主播码" />
        </el-form-item>
        <el-form-item label="所属上级主播" prop="parentAnchorId">
          <el-select v-model="anchorForm.parentAnchorId" placeholder="请选择上级主播">
            <el-option
              v-for="item in parentAnchorList"
              :key="item.id"
              :label="item.account"
              :value="item.id"
            />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'

// 模拟上级主播列表（用于下拉选择）
const parentAnchorList = ref([
  { id: 1, account: '总主播001' },
  { id: 2, account: '总主播002' },
  { id: 3, account: '总主播003' }
])

// 模拟主播列表数据
const anchorList = ref([])
// 搜索关键词
const searchKeyword = ref('')
// 分页相关
const currentPage = ref(1)
const pageSize = ref(10)
const total = ref(0)
// 弹窗相关
const dialogVisible = ref(false)
const isEdit = ref(false)
// 表单相关
const anchorFormRef = ref(null)
const anchorForm = reactive({
  id: '',
  account: '',
  password: '',
  anchorCode: '',
  parentAnchorId: ''
})
// 表单验证规则
const anchorRules = reactive({
  account: [{ required: true, message: '请输入主播账号', trigger: 'blur' }],
  password: [{ required: true, message: '请输入主播密码', trigger: 'blur' }],
  anchorCode: [{ required: true, message: '请输入主播码', trigger: 'blur' }],
  parentAnchorId: [{ required: true, message: '请选择上级主播', trigger: 'change' }]
})

// 初始化模拟数据
function initMockData() {
  const mockData = [
    { id: 1, account: 'zhubo001', password: '123456', anchorCode: 'ZC001', parentAnchorId: 1, parentAnchorName: '总主播001' },
    { id: 2, account: 'zhubo002', password: '654321', anchorCode: 'ZC002', parentAnchorId: 1, parentAnchorName: '总主播001' },
    { id: 3, account: 'zhubo003', password: '987654', anchorCode: 'ZC003', parentAnchorId: 2, parentAnchorName: '总主播002' },
    { id: 4, account: 'zhubo004', password: '876543', anchorCode: 'ZC004', parentAnchorId: 3, parentAnchorName: '总主播003' },
    { id: 5, account: 'zhubo005', password: '765432', anchorCode: 'ZC005', parentAnchorId: 2, parentAnchorName: '总主播002' }
  ]
  anchorList.value = mockData
  total.value = mockData.length
}

// 搜索功能
function handleSearch() {
  currentPage.value = 1 // 搜索时重置页码
  if (!searchKeyword.value) {
    initMockData()
    return
  }
  // 模拟搜索过滤
  const filteredData = anchorList.value.filter(item => 
    item.account.includes(searchKeyword.value) || item.anchorCode.includes(searchKeyword.value)
  )
  anchorList.value = filteredData
  total.value = filteredData.length
}

// 分页大小改变
function handleSizeChange(val) {
  pageSize.value = val
  // 实际项目中这里需要重新请求接口
}

// 页码改变
function handleCurrentChange(val) {
  currentPage.value = val
  // 实际项目中这里需要重新请求接口
}

// 打开新增弹窗
function openAddDialog() {
  isEdit.value = false
  dialogVisible.value = true
  resetForm()
}

// 打开编辑弹窗
function openEditDialog(row) {
  isEdit.value = true
  dialogVisible.value = true
  // 复制行数据到表单
  Object.assign(anchorForm, {
    id: row.id,
    account: row.account,
    password: row.password,
    anchorCode: row.anchorCode,
    parentAnchorId: row.parentAnchorId
  })
}

// 重置表单
function resetForm() {
  anchorFormRef.value?.resetFields()
  Object.assign(anchorForm, {
    id: '',
    account: '',
    password: '',
    anchorCode: '',
    parentAnchorId: ''
  })
}

// 提交表单（新增/编辑）
async function submitForm() {
  try {
    // 表单验证
    await anchorFormRef.value.validate()
    
    if (isEdit.value) {
      // 模拟编辑请求
      const index = anchorList.value.findIndex(item => item.id === anchorForm.id)
      if (index > -1) {
        // 补全上级主播名称
        const parentAnchor = parentAnchorList.value.find(item => item.id === anchorForm.parentAnchorId)
        anchorList.value[index] = {
          ...anchorForm,
          parentAnchorName: parentAnchor?.account || ''
        }
        ElMessage.success('编辑成功！')
      }
    } else {
      // 模拟新增请求
      const newId = Math.max(...anchorList.value.map(item => item.id)) + 1
      const parentAnchor = parentAnchorList.value.find(item => item.id === anchorForm.parentAnchorId)
      anchorList.value.unshift({
        id: newId,
        ...anchorForm,
        parentAnchorName: parentAnchor?.account || ''
      })
      total.value += 1
      ElMessage.success('新增成功！')
    }
    
    dialogVisible.value = false
  } catch (error) {
    console.error('表单验证失败：', error)
  }
}

// 删除主播
function handleDelete(id) {
  ElMessageBox.confirm(
    '此操作将永久删除该主播信息, 是否继续?',
    '提示',
    {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    }
  ).then(() => {
    // 模拟删除请求
    const index = anchorList.value.findIndex(item => item.id === id)
    if (index > -1) {
      anchorList.value.splice(index, 1)
      total.value -= 1
      ElMessage.success('删除成功!')
    }
  }).catch(() => {
    ElMessage.info('已取消删除')
  })
}

// 多选框选择事件（可扩展批量操作）
function handleSelectionChange(val) {
  console.log('选中的主播：', val)
}

// 页面挂载时初始化数据
onMounted(() => {
  initMockData()
})
</script>

<style scoped>
.anchor-management {
  padding: 20px;
}

.search-bar {
  display: flex;
  align-items: center;
}
</style>